import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { SignupService } from 'src/app/services/signup/signup.service';

@Component({
  selector: 'app-signup-unified-password',
  templateUrl: './signup-unified-password.component.html',
  styleUrls: ['./signup-unified-password.component.scss'],
})
export class SignupUnifiedPasswordComponent implements OnInit {
  form = this.formBuilder.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          (control: FormControl) => {
            const password = control.value;

            if (!password) return null;

            if (!this.hasNumber(password)) return { noNumber: true };

            if (!this.hasLowerCase(password)) return { noLowerCase: true };

            if (!this.hasUpperCase(password)) return { noUpperCase: true };

            if (!this.hasSpecialCharacter(password))
              return { noSpecialCharacter: true };

            return null;
          },
        ],
      ],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: this.passwordsMatchValidator }
  );

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService
  ) {}

  ngOnInit(): void {
    // Carrega dados salvos, se existirem
    const savedPasswordData = this.signupService.getBasicFormData('password');
    
    // Se não houver dados salvos ou se a senha estiver vazia, limpa o formulário
    if (!savedPasswordData || !savedPasswordData.password) {
      this.form.reset();
      this.signupService.setBasicFormStatus('password', false);
    } else {
      // Se houver dados salvos, preenche o formulário
      this.form.patchValue({
        password: savedPasswordData.password || '',
        confirmPassword: '',
      }, { emitEvent: false });
    }

    // Revalida confirmPassword quando password mudar
    this.form.controls.password.valueChanges.subscribe(() => {
      this.form.controls.confirmPassword.updateValueAndValidity();
      // Se confirmPassword já foi preenchido, marca como touched para mostrar erro
      if (this.form.controls.confirmPassword.value) {
        this.form.controls.confirmPassword.markAsTouched();
      }
    });

    // Marca confirmPassword como touched quando o valor muda e há erro de mismatch
    this.form.controls.confirmPassword.valueChanges.subscribe(() => {
      if (this.form.errors && this.form.errors['passwordsMismatch']) {
        this.form.controls.confirmPassword.markAsTouched();
      }
    });

    this.form.valueChanges.subscribe((values) => {
      // Remove confirmPassword dos dados salvos (não precisa salvar)
      const { confirmPassword, ...passwordData } = values;
      this.signupService.setBasicFormData('password', passwordData);
    });

    this.form.statusChanges.subscribe((status) => {
      this.signupService.setBasicFormStatus('password', status === 'VALID');
    });
  }

  passwordsMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordsMismatch: true };
    }

    return null;
  }

  hasLowerCase(str: string) {
    return str.toUpperCase() !== str;
  }

  hasUpperCase(str: string) {
    return str.toLowerCase() !== str;
  }

  hasSpecialCharacter(str: string) {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(str);
  }

  hasNumber(str: string) {
    return /\d/.test(str);
  }

  passwordStrength(): number[] {
    const password = this.form.get('password')?.value ?? '';

    const strengthSteps = [];

    if (password !== '') strengthSteps.push(0);
    if (password.length >= 8) strengthSteps.push(1);
    if (this.hasNumber(password)) strengthSteps.push(2);
    if (this.hasLowerCase(password) && this.hasUpperCase(password))
      strengthSteps.push(3);
    if (this.hasSpecialCharacter(password)) strengthSteps.push(4);

    return strengthSteps;
  }

  passwordStrengthPercentage(): number {
    return (100 * this.passwordStrength().length) / 5;
  }

  passwordStrengthText(): string {
    const percentage = this.passwordStrengthPercentage();

    if (percentage > 60 && percentage < 90) {
      return 'Sua senha precisa melhorar';
    } else if (percentage >= 90) {
      return 'Sua senha parece bem segura';
    }

    return 'Sua senha está fraca';
  }
}
